import { create } from 'zustand';
import {
  createJSONStorage,
  persist,
  subscribeWithSelector,
} from 'zustand/middleware';

import {
  DeviceCode,
  EventHeartRate,
  ExposureEvent,
  Phase,
  PhaseEvent,
} from '@types';
import { STORE_KEYS } from '../utils/storeCleanup';

interface HeartRateStateData {
  sessionId: string | null;
  events: EventHeartRate[];
  latestByDevice: Record<DeviceCode, EventHeartRate | undefined>;
  phase: Phase;
  phaseEvents: PhaseEvent[];
  exposureEvents: ExposureEvent[];
}

interface HeartRateStateActions {
  addEvent: (e: EventHeartRate) => void;
  setPhase: (p: Phase) => void;
  setPhaseEvent: (p: Phase, hr: number) => void;
  addExposureEvent: (e: ExposureEvent) => void;
  getLatestHeartRate: (deviceCode?: DeviceCode) => number | null;
  clearAllEvents: () => void;
  /** Clears all data and sets the current session id (called when navigating to a different session) */
  clearForSession: (sessionId: string) => void;
}

type HeartRateState = HeartRateStateData & HeartRateStateActions;

const STORAGE_KEY = STORE_KEYS.HEART_RATE;

const initialState: HeartRateStateData = {
  sessionId: null,
  events: [],
  latestByDevice: {},
  phase: 'p0',
  phaseEvents: [],
  exposureEvents: [],
};

function normalizeItemIds(e: ExposureEvent): number[] {
  if (e.type !== 'itemShown' && e.type !== 'itemFeedback') return [];
  const ex = e as { itemIds?: number[]; foodIds?: number[]; foodId?: number };
  if (Array.isArray(ex.itemIds)) return ex.itemIds;
  if (Array.isArray(ex.foodIds)) return ex.foodIds;
  if (typeof ex.foodId === 'number') return [ex.foodId];
  return [];
}

function isSameExposureEvent(a: ExposureEvent, b: ExposureEvent): boolean {
  if (a.type !== b.type || a.timestamp !== b.timestamp) return false;
  const sameItemIds = (idsA: number[], idsB: number[]) =>
    idsA.length === idsB.length &&
    [...idsA].sort((x, y) => x - y).join('|') ===
      [...idsB].sort((x, y) => x - y).join('|');
  if (a.type === 'itemShown' && b.type === 'itemShown')
    return sameItemIds(normalizeItemIds(a), normalizeItemIds(b));
  if (a.type === 'itemFeedback' && b.type === 'itemFeedback')
    return (
      sameItemIds(normalizeItemIds(a), normalizeItemIds(b)) &&
      a.score === b.score
    );
  if (a.type === 'mealEnded' && b.type === 'mealEnded') {
    const clientIdA = (a as { clientId?: string }).clientId;
    const clientIdB = (b as { clientId?: string }).clientId;
    return clientIdA === clientIdB;
  }
  return true;
}

export const useHeartRateStore = create<HeartRateState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        ...initialState,

        addEvent: e => {
          const { events } = get();
          if (events.some(x => x.id === e.id)) return;

          set(state => {
            const updated = [...state.events, e];
            const latest = state.latestByDevice[e.deviceCode];
            const newer =
              !latest || e.timestamp > latest.timestamp ? e : latest;
            return {
              events: updated,
              latestByDevice: {
                ...state.latestByDevice,
                [e.deviceCode]: newer,
              },
            };
          });
        },

        setPhase: p => set({ phase: p }),

        setPhaseEvent: (p: Phase, hr: number) =>
          set(state => ({
            phaseEvents: [
              ...state.phaseEvents,
              { phase: p, timestamp: Date.now(), hr },
            ],
          })),

        addExposureEvent: (e: ExposureEvent) => {
          const { exposureEvents } = get();
          if (
            exposureEvents.some(existing => isSameExposureEvent(existing, e))
          ) {
            return;
          }
          set(state => ({
            exposureEvents: [...state.exposureEvents, e],
          }));
        },

        getLatestHeartRate: deviceCode => {
          const { latestByDevice, events } = get();
          const code = deviceCode ?? events.find(Boolean)?.deviceCode;
          if (!code) return null;

          const latest = latestByDevice[code];
          return latest?.hr ?? null;
        },

        clearAllEvents: () => {
          set(initialState);
        },

        clearForSession: (sessionId: string) => {
          set({ ...initialState, sessionId });
        },
      }),
      {
        name: STORAGE_KEY,
        storage: createJSONStorage(() => localStorage),
        partialize: s => ({
          sessionId: s.sessionId,
          // Strip window series from localStorage (1 Hz republish would exceed quota).
          // In-memory events and API hrData keep the full payload.
          events: stripWindowSeriesFromEvents(normalizeEvents(s.events)),
          eventsCount: s.events.length,
          latestByDevice: stripWindowSeriesFromLatestByDevice(
            s.latestByDevice
          ),
          phase: s.phase,
          phaseEvents: s.phaseEvents,
          phaseEventsCount: s.phaseEvents.length,
          exposureEvents: s.exposureEvents,
          exposureEventsCount: s.exposureEvents.length,
        }),
        version: 2,
        migrate: (persistedState: unknown) => {
          const s = persistedState as Record<string, unknown>;
          return {
            ...s,
            sessionId: s?.sessionId ?? null,
          };
        },
      }
    )
  )
);

const numberOrZero = (value: number | null | undefined): number | null => {
  if (value === null) return null;
  if (value === undefined) return 0;
  return Number.isFinite(value) ? value : 0;
};

/** Window series republished at 1 Hz — omit from localStorage only. */
const WINDOW_SERIES_KEYS = [
  'ppisMs',
  'ppiSamples',
  'tachogramSamples',
  'ppiTimesSec',
  'rawIncludedPpiMs',
  'correctedPpiSeries',
  'resampledSeries',
] as const;

const omitWindowSeries = <T extends Record<string, unknown>>(
  event: T
): Omit<T, (typeof WINDOW_SERIES_KEYS)[number]> => {
  const next = { ...event };
  for (const key of WINDOW_SERIES_KEYS) {
    delete next[key];
  }
  return next;
};

const stripWindowSeriesFromEvents = (
  events: Record<string, unknown>[]
): Record<string, unknown>[] => events.map(omitWindowSeries);

const stripWindowSeriesFromLatestByDevice = (
  latestByDevice: Record<DeviceCode, EventHeartRate | undefined>
): Record<DeviceCode, EventHeartRate | undefined> => {
  const next: Record<DeviceCode, EventHeartRate | undefined> = {
    ...latestByDevice,
  };
  for (const code of Object.keys(next) as DeviceCode[]) {
    const event = next[code];
    if (event) {
      next[code] = omitWindowSeries(
        event as unknown as Record<string, unknown>
      ) as unknown as EventHeartRate;
    }
  }
  return next;
};

/** Fields persisted into DeviceConfigurations.hrData (charts + Polar extras). */
export const normalizeEvents = (events: EventHeartRate[]) => {
  return events.map(e => {
    const normalized: Record<string, unknown> = {
      hr: e.hr || 0,
      hrv: numberOrZero(e.hrv),
      lf: numberOrZero(e.lf),
      hf: numberOrZero(e.hf),
      timestamp: e.timestamp,
    };

    if (e.deviceCode !== undefined) normalized.deviceCode = e.deviceCode;
    if (e.deviceId !== undefined) normalized.deviceId = e.deviceId;
    if (e.deviceModel !== undefined) normalized.deviceModel = e.deviceModel;
    if (e.lfPower !== undefined) normalized.lfPower = e.lfPower;
    if (e.hfPower !== undefined) normalized.hfPower = e.hfPower;
    if (e.lfHfRatio !== undefined) normalized.lfHfRatio = e.lfHfRatio;
    if (e.lfHfValid !== undefined) normalized.lfHfValid = e.lfHfValid;
    if (e.lfHfStatus !== undefined) normalized.lfHfStatus = e.lfHfStatus;
    if (e.lfHfAcceptedRatio !== undefined) {
      normalized.lfHfAcceptedRatio = e.lfHfAcceptedRatio;
    }
    if (e.lfHfMaxArtifactGapSec !== undefined) {
      normalized.lfHfMaxArtifactGapSec = e.lfHfMaxArtifactGapSec;
    }
    if (e.lfHfComputedAt !== undefined) {
      normalized.lfHfComputedAt = e.lfHfComputedAt;
    }
    if (e.rrMs !== undefined) normalized.rrMs = e.rrMs;
    if (e.rrSource !== undefined) normalized.rrSource = e.rrSource;
    if (e.ppiMs !== undefined) normalized.ppiMs = e.ppiMs;
    if (e.ppisMs !== undefined) normalized.ppisMs = e.ppisMs;
    if (e.ppiSamples !== undefined) normalized.ppiSamples = e.ppiSamples;
    if (e.tachogramSamples !== undefined) {
      normalized.tachogramSamples = e.tachogramSamples;
    }
    if (e.ppiTimesSec !== undefined) normalized.ppiTimesSec = e.ppiTimesSec;
    if (e.rawIncludedPpiMs !== undefined) {
      normalized.rawIncludedPpiMs = e.rawIncludedPpiMs;
    }
    if (e.correctedPpiSeries !== undefined) {
      normalized.correctedPpiSeries = e.correctedPpiSeries;
    }
    if (e.resampledSeries !== undefined) {
      normalized.resampledSeries = e.resampledSeries;
    }
    if (e.includedIntervalDurationSec !== undefined) {
      normalized.includedIntervalDurationSec = e.includedIntervalDurationSec;
    }
    if (e.tachogramDomainSec !== undefined) {
      normalized.tachogramDomainSec = e.tachogramDomainSec;
    }
    if (e.resampledDurationSec !== undefined) {
      normalized.resampledDurationSec = e.resampledDurationSec;
    }
    if (e.hrvWindowSeconds !== undefined) {
      normalized.hrvWindowSeconds = e.hrvWindowSeconds;
    }
    if (e.hrvIntervalCount !== undefined) {
      normalized.hrvIntervalCount = e.hrvIntervalCount;
    }
    if (e.hrvMethod !== undefined) normalized.hrvMethod = e.hrvMethod;
    if (e.hrvSamplingHz !== undefined) {
      normalized.hrvSamplingHz = e.hrvSamplingHz;
    }
    if (e.livePublishHz !== undefined) {
      normalized.livePublishHz = e.livePublishHz;
    }
    if (e.peakFrequencyHz !== undefined) {
      normalized.peakFrequencyHz = e.peakFrequencyHz;
    }
    if (e.lfBandHz !== undefined) normalized.lfBandHz = e.lfBandHz;
    if (e.hfBandHz !== undefined) normalized.hfBandHz = e.hfBandHz;
    if (e.hrvPreprocessing !== undefined) {
      normalized.hrvPreprocessing = e.hrvPreprocessing;
    }
    if (e.skinTemperatureC !== undefined) {
      normalized.skinTemperatureC = e.skinTemperatureC;
    }
    if (e.contactStatus !== undefined) {
      normalized.contactStatus = e.contactStatus;
    }
    if (e.contactStatusSupported !== undefined) {
      normalized.contactStatusSupported = e.contactStatusSupported;
    }
    if (e.errorEstimate !== undefined) {
      normalized.errorEstimate = e.errorEstimate;
    }
    if (e.blocker !== undefined) normalized.blocker = e.blocker;
    if (e.ppgQuality !== undefined) normalized.ppgQuality = e.ppgQuality;
    if (e.correctedHr !== undefined) normalized.correctedHr = e.correctedHr;
    if (e.rrAvailable !== undefined) normalized.rrAvailable = e.rrAvailable;
    if (e.rrsMs !== undefined) normalized.rrsMs = e.rrsMs;
    if (e.appVersion !== undefined) normalized.appVersion = e.appVersion;

    return normalized;
  });
};
